import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";

/**
 * SchemaFormRenderer — renderiza un formulario a partir del schema devuelto
 * por /api/perfil/schema/{tipo}. Reutilizable en todo el ecosistema.
 *
 * Regla UX crítica (Etapa 3 §6.9): un solo campo activo, auto-guardado on blur.
 * Los grupos se muestran como secciones con separador.
 */

const GROUP_LABELS = {
  empresa: "Datos de la empresa",
  identidad: "Identidad profesional",
  servicio: "Servicio",
  experiencia: "Experiencia",
  operacion: "Operación",
  catalogo: "Catálogo",
  preferencias: "Preferencias",
};

export default function SchemaFormRenderer({ schema, values, onFieldBlur, disabled = false, testidPrefix = "sfr" }) {
  const [local, setLocal] = useState(values || {});

  useEffect(() => {
    setLocal(values || {});
  }, [values]);

  if (!schema) return null;

  const commit = (nombre, next) => {
    // Comparación estricta contra el valor que llegó como prop (values).
    // Para booleans y null usamos igualdad JSON con normalización de vacíos.
    const current = values?.[nombre];
    const norm = (v) => (v === undefined || v === "" ? null : v);
    if (JSON.stringify(norm(next)) === JSON.stringify(norm(current))) return;
    onFieldBlur?.(nombre, next);
  };

  // Agrupar campos
  const grouped = schema.campos.reduce((acc, c) => {
    const g = c.grupo || "otros";
    acc[g] = acc[g] || [];
    acc[g].push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid={testidPrefix}>
      {Object.entries(grouped).map(([grupo, campos]) => (
        <section key={grupo} data-testid={`${testidPrefix}-group-${grupo}`}>
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
            {GROUP_LABELS[grupo] || grupo}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campos.map((c) => (
              <FieldRenderer
                key={c.nombre}
                field={c}
                value={local[c.nombre]}
                onChange={(v) => setLocal((s) => ({ ...s, [c.nombre]: v }))}
                onBlur={(v) => commit(c.nombre, v)}
                disabled={disabled}
                testid={`${testidPrefix}-field-${c.nombre}`}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function FieldRenderer({ field, value, onChange, onBlur, disabled, testid }) {
  const { nombre, etiqueta, tipo, requerido } = field;

  const label = (
    <label htmlFor={nombre} className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
      {etiqueta}
      {requerido && <span className="text-rose-600" aria-hidden>*</span>}
    </label>
  );

  const wide = tipo === "multi-select" || tipo === "textarea";

  return (
    <div className={`flex flex-col ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
      {label}
      {tipo === "text" && (
        <input
          id={nombre}
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlur(e.target.value.trim())}
          disabled={disabled}
          maxLength={field.max_length}
          className="input-field"
          data-testid={`${testid}-input`}
        />
      )}
      {tipo === "number" && (
        <input
          id={nombre}
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          onBlur={(e) => onBlur(e.target.value === "" ? null : Number(e.target.value))}
          min={field.min}
          max={field.max}
          disabled={disabled}
          className="input-field"
          data-testid={`${testid}-input`}
        />
      )}
      {tipo === "textarea" && (
        <textarea
          id={nombre}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlur(e.target.value.trim())}
          maxLength={field.max_length}
          disabled={disabled}
          className="input-field min-h-[80px]"
          data-testid={`${testid}-input`}
        />
      )}
      {tipo === "select" && (
        <select
          id={nombre}
          value={value ?? ""}
          onChange={(e) => { onChange(e.target.value); onBlur(e.target.value || null); }}
          disabled={disabled}
          className="input-field"
          data-testid={`${testid}-select`}
        >
          <option value="">Selecciona una opción</option>
          {(field.opciones || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
      {tipo === "multi-select" && (
        <div
          className="flex flex-wrap gap-2 border border-slate-300 rounded-lg p-2 bg-white"
          data-testid={`${testid}-multi`}
        >
          {(field.opciones || []).map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const active = arr.includes(o.value);
            return (
              <button
                type="button"
                key={o.value}
                disabled={disabled}
                onClick={() => {
                  const next = active ? arr.filter((v) => v !== o.value) : [...arr, o.value];
                  onChange(next);
                  onBlur(next);
                }}
                className={[
                  "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors",
                  active
                    ? "bg-brand-blue text-white border-brand-blue"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                  disabled ? "opacity-50 cursor-not-allowed" : "",
                ].join(" ")}
                data-testid={`${testid}-opt-${o.value}`}
              >
                {active && <Check size={12} />}
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {tipo === "boolean" && (
        <label className="inline-flex items-center gap-2 mt-1 cursor-pointer" data-testid={`${testid}-bool`}>
          <input
            id={nombre}
            type="checkbox"
            checked={!!value}
            onChange={(e) => { onChange(e.target.checked); onBlur(e.target.checked); }}
            disabled={disabled}
            className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
          />
          <span className="text-sm text-slate-700">Activado</span>
        </label>
      )}
    </div>
  );
}
